import zod from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Grid2 } from '@mui/material';
import TextFieldComponent from '../../../components/TextFieldComponent';
import CheckboxComponent from '../../../components/CheckboxComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import { useContext, useEffect } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import { MoovsecIntegrationFormSchema } from '../../../services/IntegrationService/MoovsecService/schemas';
import {
    useCreateMoovsecIntegrationAsync,
    useGetMoovsecIntegrationByIdAsync,
    useUpdateMoovsecIntegrationAsync,
} from '../../../services/IntegrationService/MoovsecService/hooks/useMutation';

interface MoovsecEditionFormProps {
    moovsecIdToUpdate?: number;
    handleSaveForm?: () => void;
}

type MoovsecEditionFormInput = zod.infer<typeof MoovsecIntegrationFormSchema>;

const MoovsecEditionForm = ({
    moovsecIdToUpdate,
    handleSaveForm,
}: MoovsecEditionFormProps) => {
    const { showSnackbar } = useContext(AppContext);
    const { control, handleSubmit, reset } = useForm<MoovsecEditionFormInput>({
        resolver: zodResolver(MoovsecIntegrationFormSchema),
        defaultValues: {
            name: '',
            description: '',
            isActive: true,
            baseUrl: '',
            login: '',
            password: '',
        },
    });

    const { mutate: mutateCreate, isLoading: isLoadingCreate } =
        useCreateMoovsecIntegrationAsync();
    const { mutate: mutateUpdate, isLoading: isLoadingUpdate } =
        useUpdateMoovsecIntegrationAsync();
    const { mutateAsync: mutateGetById, isLoading: isLoadingGetById } =
        useGetMoovsecIntegrationByIdAsync();

    const saveSuccess = (message: string) => {
        reset();
        showSnackbar(message, 'success');
        if (handleSaveForm) handleSaveForm();
    };

    const handleSave = (data: MoovsecEditionFormInput) => {
        if (moovsecIdToUpdate) {
            mutateUpdate(
                { id: moovsecIdToUpdate, data },
                {
                    onSuccess: () => saveSuccess('Integração Moovsec atualizada com sucesso.'),
                },
            );
            return;
        }

        mutateCreate(data, {
            onSuccess: () => saveSuccess('Integração Moovsec criada com sucesso.'),
        });
    };

    useEffect(() => {
        if (!moovsecIdToUpdate) {
            return;
        }

        let isMounted = true;

        mutateGetById(moovsecIdToUpdate).then((response) => {
            if (!isMounted) {
                return;
            }

            reset(response, { keepDefaultValues: true });
        });

        return () => {
            isMounted = false;
        };
    }, [moovsecIdToUpdate, mutateGetById, reset]);

    return (
        <Grid2 container columnSpacing={2} rowSpacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
                <Controller
                    name="name"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <TextFieldComponent
                            value={value}
                            errorMessage={error?.message}
                            onChangeText={onChange}
                            label="Nome"
                        />
                    )}
                />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
                <Controller
                    name="baseUrl"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <TextFieldComponent
                            value={value}
                            errorMessage={error?.message}
                            onChangeText={onChange}
                            label="Base URL"
                        />
                    )}
                />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
                <Controller
                    name="login"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <TextFieldComponent
                            value={value}
                            errorMessage={error?.message}
                            onChangeText={onChange}
                            label="Usuário"
                        />
                    )}
                />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
                <Controller
                    name="password"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <TextFieldComponent
                            value={value}
                            errorMessage={error?.message}
                            onChangeText={onChange}
                            label="Senha"
                            type="password"
                        />
                    )}
                />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
                <Controller
                    name="description"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <TextFieldComponent
                            value={value}
                            errorMessage={error?.message}
                            onChangeText={onChange}
                            label="Descrição"
                        />
                    )}
                />
            </Grid2>

            <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
                <Controller
                    name="isActive"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <CheckboxComponent value={value} onChange={onChange} label="Ativo" />
                    )}
                />
            </Grid2>

            <Grid2 size={12} sx={{ mt: 2 }}>
                <ButtonComponent
                    fullWidth
                    onClick={handleSubmit(handleSave)}
                    loading={isLoadingCreate || isLoadingUpdate || isLoadingGetById}
                >
                    Salvar
                </ButtonComponent>
            </Grid2>
        </Grid2>
    );
};

export default MoovsecEditionForm;
